/**
 * MCP Servers Panel Component
 * List and manage MCP servers
 * Matches the old admin interface
 */

import React, { useState } from 'react';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { Textarea } from '@/components/ui/Textarea';
import { Loading } from '@/components/common';
import { Plus, RefreshCw, AlertCircle, AlertTriangle, Code, X } from 'lucide-react';
import { MCPServerCard } from './MCPServerCard';
import { MCPServerForm } from './MCPServerForm';
import { useMCPServers, useMCPConfig, useUpdateMCPConfig, useReloadMCPManager } from '../hooks/useSystemConfig';
import { useI18n } from '@/i18n';
import type { MCPServer, MCPServerConfig } from '@/types/system';

export const MCPServersPanel: React.FC = () => {
  const { messages } = useI18n();
  const t = messages.systemSettings.mcp;
  const [editingServer, setEditingServer] = useState<MCPServer | null>(null);
  const [isAdding, setIsAdding] = useState(false);
  const [showJsonEditor, setShowJsonEditor] = useState(false);
  const [jsonEditorValue, setJsonEditorValue] = useState('');
  const [jsonError, setJsonError] = useState<string | null>(null);

  const { data: servers, isLoading, error } = useMCPServers();
  const { data: mcpConfig } = useMCPConfig();
  const updateConfig = useUpdateMCPConfig();
  const reloadManager = useReloadMCPManager();

  const handleSave = async (serverId: string, config: MCPServerConfig) => {
    if (!mcpConfig) return;

    try {
      const newConfig = {
        mcpServers: {
          ...mcpConfig.mcpServers,
          [serverId]: config,
        },
      };

      await updateConfig.mutateAsync(newConfig);
      setEditingServer(null);
      setIsAdding(false);
    } catch (error) {
      alert(`${t?.saveFailed}: ${error}`);
    }
  };

  const handleDelete = async (serverId: string) => {
    if (!mcpConfig) return;

    try {
      const newServers = { ...mcpConfig.mcpServers };
      delete newServers[serverId];

      await updateConfig.mutateAsync({
        mcpServers: newServers,
      });
    } catch (error) {
      alert(`${t?.deleteFailed}: ${error}`);
    }
  };

  const handleReload = async () => {
    try {
      await reloadManager.mutateAsync();
      alert(t?.reloadSuccess);
    } catch (error) {
      alert(`${t?.reloadFailed}: ${error}`);
    }
  };

  const handleOpenJsonEditor = () => {
    if (mcpConfig) {
      // Format the mcpServers object as JSON
      const configToEdit = { mcpServers: mcpConfig.mcpServers || {} };
      setJsonEditorValue(JSON.stringify(configToEdit, null, 2));
      setJsonError(null);
      setShowJsonEditor(true);
    }
  };

  const handleSaveJsonEditor = async () => {
    try {
      const parsed = JSON.parse(jsonEditorValue);
      if (!parsed.mcpServers || typeof parsed.mcpServers !== 'object') {
        setJsonError(t?.jsonMustHaveMcpServers || 'JSON must have a "mcpServers" object');
        return;
      }
      await updateConfig.mutateAsync(parsed);
      setShowJsonEditor(false);
      alert(t?.jsonSaveSuccess);
    } catch (e) {
      if (e instanceof SyntaxError) {
        setJsonError(`Invalid JSON: ${e.message}`);
      } else {
        setJsonError(`${t?.saveFailed}: ${(e as Error).message}`);
      }
    }
  };

  if (isLoading) {
    return <Loading message={t?.loading} />;
  }

  if (error) {
    return (
      <div className="flex items-center gap-2 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
        <AlertCircle className="w-5 h-5" />
        <span>{t?.loadFailed}: {error.message}</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Warning Banner */}
      <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4">
        <div className="flex">
          <AlertTriangle className="w-5 h-5 text-yellow-400 flex-shrink-0" />
          <div className="ml-3">
            <p className="text-sm text-yellow-700">
              {t?.warningBanner}
            </p>
          </div>
        </div>
      </div>

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">{t?.title}</h2>
          <p className="text-sm text-gray-600 mt-1">
            {t?.description}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="secondary"
            onClick={handleOpenJsonEditor}
          >
            <Code className="w-4 h-4 mr-2" />
            {t?.editJson}
          </Button>
          <Button
            variant="secondary"
            onClick={handleReload}
            disabled={reloadManager.isPending}
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${reloadManager.isPending ? 'animate-spin' : ''}`} />
            {t?.reloadManager}
          </Button>
          <Button onClick={() => setIsAdding(true)} disabled={isAdding || !!editingServer}>
            <Plus className="w-4 h-4 mr-2" />
            {t?.addServer}
          </Button>
        </div>
      </div>

      {/* Add/Edit Form */}
      {(isAdding || editingServer) && (
        <MCPServerForm
          server={editingServer || undefined}
          onSave={handleSave}
          onCancel={() => {
            setIsAdding(false);
            setEditingServer(null);
          }}
        />
      )}

      {/* Server List */}
      {servers && servers.length > 0 ? (
        <div className="space-y-3">
          {servers.map((server) => (
            <MCPServerCard
              key={server.id}
              server={server}
              onEdit={setEditingServer}
              onDelete={handleDelete}
            />
          ))}
        </div>
      ) : (
        <div className="text-center py-12 bg-gray-50 rounded-lg border border-gray-200">
          <p className="text-gray-600">{t?.noServers}</p>
          <button
            onClick={() => setIsAdding(true)}
            className="mt-2 text-blue-600 hover:text-blue-700 font-medium"
          >
            {t?.addFirstServer}
          </button>
        </div>
      )}

      {/* Info */}
      <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-800">
        <p className="font-medium mb-2">{t?.aboutMcp}</p>
        <ul className="space-y-1 list-disc list-inside">
          {t?.aboutMcpItems?.map((item, idx) => (
            <li key={idx}>{item}</li>
          ))}
        </ul>
      </div>

      {/* Metadata */}
      {mcpConfig?.version !== undefined && (
        <div className="text-sm text-gray-500">
          <p>
            {t?.version}: <span>{mcpConfig.version}</span> |
            {t?.lastUpdatedBy}: <span>{mcpConfig.updated_by || 'N/A'}</span> |
            <span> {mcpConfig.updated_at ? new Date(mcpConfig.updated_at).toLocaleString() : 'N/A'}</span>
          </p>
        </div>
      )}

      {/* JSON Editor Modal */}
      {showJsonEditor && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <Card className="w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
            <div className="flex items-center justify-between p-4 border-b">
              <h3 className="text-lg font-semibold">{t?.jsonEditorTitle}</h3>
              <button
                onClick={() => setShowJsonEditor(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-4 flex-1 overflow-auto">
              <Textarea
                value={jsonEditorValue}
                onChange={(e) => {
                  setJsonEditorValue(e.target.value);
                  setJsonError(null);
                }}
                rows={25}
                className="font-mono text-xs w-full"
                spellCheck={false}
              />
              {jsonError && (
                <div className="mt-2 text-sm text-red-600">
                  {jsonError}
                </div>
              )}
            </div>
            <div className="flex items-center justify-between p-4 border-t bg-gray-50">
              <p className="text-xs text-gray-500">
                {t?.jsonEditorHint}
              </p>
              <div className="flex gap-2">
                <Button variant="secondary" onClick={() => setShowJsonEditor(false)}>
                  {t?.cancel}
                </Button>
                <Button onClick={handleSaveJsonEditor} disabled={updateConfig.isPending}>
                  {updateConfig.isPending ? t?.saving : t?.saveConfiguration}
                </Button>
              </div>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
};
